import { useState, useEffect, useRef } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../firebase';
import { useAuth } from '../../context/Auth/auth-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { joinSession, saveDisplayName } from '../../services/live-session';
import { track } from '../../services/analytics';
import { AVATARS, isAvatar } from '../../constants/avatars';

export const JoinQuiz = () => {
  // Deep link: the broadcast QR encodes /?pin=XXXXXX, so a scanned phone lands
  // here with the PIN already filled (it also survives the sign-in redirect —
  // ProtectedRoute preserves the query string through /login).
  const [searchParams] = useSearchParams();
  const [sessionCode, setSessionCode] = useState(
    () => (searchParams.get('pin') ?? '').replace(/\D/g, '').slice(0, 6)
  );
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { auth, userInfo } = useAuth();
  const navigate = useNavigate();
  const nicknameTouched = useRef(false);
  const avatarTouched = useRef(false);

  // Prefill from the saved profile name (users/{uid}/name persists the player's
  // last pick), falling back to the Google display name — but never clobber
  // something the player has already typed.
  useEffect(() => {
    if (nicknameTouched.current || nickname) return;
    const suggested = userInfo?.name || auth?.currentUser?.displayName || '';
    if (suggested) setNickname(suggested);
  }, [userInfo?.name, auth, nickname]);

  // Same deal for the saved avatar (users/{uid}/avatar) — persistent identity,
  // the same face on every board.
  useEffect(() => {
    if (avatarTouched.current || avatar) return;
    if (isAvatar(userInfo?.avatar)) setAvatar(userInfo.avatar);
  }, [userInfo?.avatar, avatar]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    const current = auth?.currentUser;
    if (!current?.uid) return;

    setLoading(true);
    setError(null);

    try {
      // Check the session exists (read-once; we don't subscribe here).
      const sessionSnapshot = await get(ref(db, `sessions/${sessionCode}`));
      if (!sessionSnapshot.exists()) throw new Error('Invalid game PIN');
      if (sessionSnapshot.val().status === 'ended') throw new Error('This game has already ended');

      // Denormalize the display name (+ avatar) so the host never N+1 fetches
      // users/{uid}.
      const name = nickname.trim() || current.displayName || userInfo?.name || 'Player';
      await joinSession(sessionCode, current.uid, name, avatar || undefined);
      track('join_game', {
        mode: sessionSnapshot.val().youtubeUrl ? 'stream' : 'app',
        via: searchParams.get('pin') ? 'deep_link' : 'typed',
      });

      // Remember the nickname + avatar for next time (persistent identity — the
      // same name and face show up on next week's leaderboard). Non-blocking: a
      // failure here must not stop the join.
      saveDisplayName(current.uid, name, avatar || undefined).catch(() => {});

      navigate(`/quiz/${sessionCode}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join quiz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stage flex items-center justify-center px-4 py-12">
      <div className="card w-full max-w-md p-8 animate-pop">
        <h1 className="text-3xl font-extrabold font-display tracking-tight text-center">Join the game</h1>
        <p className="text-center text-slate-500 mt-1 mb-6">Enter the PIN shown on the stream.</p>
        <form onSubmit={handleJoin}>
          <input
            type="text"
            inputMode="numeric"
            value={sessionCode}
            onChange={(e) => { setSessionCode(e.target.value.replace(/\D/g, '')); setError(null); }}
            placeholder="000000"
            className="w-full text-center font-display text-4xl font-bold tracking-[0.4em] py-4 rounded-xl border-2 border-slate-200 focus:border-ink focus:outline-none"
            maxLength={6}
            autoFocus
          />
          <label className="block mt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Your name on the leaderboard
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => { nicknameTouched.current = true; setNickname(e.target.value); }}
            placeholder="Pick a name"
            maxLength={24}
            className="w-full mt-1 text-center text-lg font-semibold py-3 rounded-xl border-2 border-slate-200 focus:border-ink focus:outline-none"
          />
          <label className="block mt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Pick your face
          </label>
          <div className="grid grid-cols-6 gap-2 mt-1">
            {AVATARS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => { avatarTouched.current = true; setAvatar((cur) => (cur === a ? '' : a)); }}
                aria-pressed={avatar === a}
                className={`aspect-square grid place-items-center text-2xl rounded-xl border-2 transition ${
                  avatar === a
                    ? 'border-ink bg-accent/30 scale-105'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <button type="submit" disabled={loading || sessionCode.length < 6} className="btn-primary w-full mt-4 text-lg">
            {loading ? 'Joining…' : 'Enter'}
          </button>
          {error && <p className="text-rose-600 text-center mt-3 font-medium">{error}</p>}
        </form>
      </div>
    </div>
  );
};
